/**
 * Light, dark, or whatever the phone is set to.
 *
 * This is an interface preference, not a wish: it says nothing about the will, so it is kept in
 * plain local storage beside the language rather than in the encrypted vault. The rules for
 * reading and applying it live here, with no DOM in sight, so they can be tested as what they
 * are — three values and a decision.
 */
export const THEME_CHOICES = ['auto', 'light', 'dark'] as const;

export type ThemeChoice = (typeof THEME_CHOICES)[number];

/** Kept in sync by hand with the boot script in index.html, which runs before Angular exists. */
export const THEME_STORAGE_KEY = 'mon-testament-theme';

/**
 * Light is the default: it is the appearance the app was drawn in, and most readers never open
 * this setting at all. Following the phone and night stay one tap away in Settings.
 */
export const DEFAULT_THEME: ThemeChoice = 'light';

export const isThemeChoice = (value: unknown): value is ThemeChoice =>
  typeof value === 'string' && (THEME_CHOICES as readonly string[]).includes(value);

/**
 * Anything else — a stale value, a hand-edited key, nothing at all — means the default. A choice
 * already made, "follow the phone" included, is kept as it is.
 */
export const normaliseTheme = (value: unknown): ThemeChoice =>
  isThemeChoice(value) ? value : DEFAULT_THEME;

/** Whether the page is actually dark, once the choice and the phone have both had their say. */
export const isNight = (choice: ThemeChoice, phonePrefersDark: boolean): boolean =>
  choice === 'dark' || (choice === 'auto' && phonePrefersDark);

/**
 * What the browser paints its own chrome with. The band is green at the top of nearly every
 * screen, so daylight keeps the green; at night the surrounding chrome follows the page instead,
 * because a bright strip above a dark screen is exactly what a reader turned dark mode on to
 * avoid.
 */
export const THEME_COLOR_DAY = '#173a2c';
export const THEME_COLOR_NIGHT = '#0d1813';

export const themeColorFor = (choice: ThemeChoice, phonePrefersDark: boolean): string =>
  isNight(choice, phonePrefersDark) ? THEME_COLOR_NIGHT : THEME_COLOR_DAY;
